<template>
  <container-loader v-if="!loaded"></container-loader>
  <div v-else>
    <b-row class="dashboard__overview" v-if="statisticLoaded">
      <b-col lg="2" md="3" sm="4" cols="6" class="dashboard__overview__item">
        <div>
          <span>{{hoursPrinted}}</span>
          <p>{{$t('history.stat.hoursPrinted')}}</p>
        </div>
      </b-col>
      <b-col lg="2"  md="3" sm="4" cols="6" class="dashboard__overview__item">
        <div>
          <span>{{statistic.prints}}</span>
          <p>{{$t('history.stat.prints')}}</p>
        </div>
      </b-col>
      <b-col lg="2"  md="3" sm="4" cols="6" class="dashboard__overview__item">
        <div>
          <span>{{statistic.printing}}</span>
          <p>{{$t('history.stat.printing')}}</p>
        </div>
      </b-col>
      <b-col lg="2"  md="3" sm="4" cols="6" class="dashboard__overview__item">
        <div>
          <span>{{statistic.queue}}</span>
          <p>{{$t('history.stat.inQueue')}}</p>
        </div>
      </b-col>
      <b-col lg="2"  md="3" sm="4" cols="6" class="dashboard__overview__item">
        <div>
          <span>{{statistic.errors}}</span>
          <p>{{$t('history.stat.errors')}}</p>
        </div>
      </b-col>
      <b-col lg="2"  md="3" sm="4" cols="6" class="dashboard__overview__item">
        <div>
          <span>{{materialsUsed}}</span>
          <p>{{$t('history.stat.materialsUsed')}}</p>
        </div>
      </b-col>
    </b-row>
    <div v-if="!count" style="text-align: center">
      <span class="text--muted">{{$t('history.noResult')}}</span>
    </div>
    <div class="table table--files table--jobs table--dashboard" v-else>
      <b-table :fields="fields" :items="jobs" stripped hover :tbody-tr-class="rowClass" primary-key="id" @row-clicked="handleRowClick">
        <template v-slot:cell(preview)="data">
          <model-preview :icon-img="data.item.picture_preview"
                         :preview-img="data.item.picture"
                         :video-link="data.item.gif_image"
                         @openTimelaps="handleOpenTimelaps(data.item.id)">
          </model-preview>
          <job-status :job-status-id="data.item.status_id"></job-status>
        </template>
        <template v-slot:cell(filename)="data">
          {{fileNameCut(data.item.filename, 25, false)}}
        </template>
        <template v-slot:cell(datetime)="data">
          <date-time-text class="edit-time" :date="data.item.datetime"></date-time-text>
          <span class="text--muted"> {{$t('history.by')}} </span>
          <span v-if="!isMyEmail(data.item.email)">{{data.item.email}}</span>
          <span v-else>{{$t('history.ownEmail')}}</span>
        </template>
        <template v-slot:cell(printer_name)="data">
          <span v-b-tooltip.hover :title="printerTypeAndMaterialText(data.item)">{{data.item.printer_name}}</span>
        </template>
        <template v-slot:cell(cost)="data">
          <job-cost :cost="data.item.cost"></job-cost>
        </template>
        <template v-slot:cell(filament)="data">
          <job-filament :filament="data.item.weight"></job-filament>
        </template>
        <template v-slot:cell(printTime)="data">
          <print-time :estimated-time="data.item.printing_duration" :print-time="data.item.print_time" :object-id="data.item.id"></print-time>
        </template>
        <template v-slot:cell(action)="data">
          <dashboard-history-action-buttons
              :ref="'actionButtons' + data.item.id"
              :job="data.item">
          </dashboard-history-action-buttons>
        </template>
      </b-table>
    </div>
    <infinite-loading @infinite="handleInfiniteLoading">
      <div slot="no-more"></div>
      <div slot="no-results"></div>
    </infinite-loading>
  </div>
</template>

<script>
  import {mapActions} from 'vuex';
  import InfiniteLoading from 'vue-infinite-loading';

  import ContainerLoader from "../ContainerLoader";
  import JobCost from "../printers/job/JobCost";
  import JobFilament from "../printers/job/JobFilament";
  import JobStatus from "../printers/job/JobStatus";
  import DashboardHistoryActionButtons from "./DashboardHistoryActionButtons";

  import FileMixin from '@/mixins/file';
  import UserMixin from '@/mixins/user';
  import ModelPreview from "../ModelPreview";
  import DateTimeText from "../DateTimeText";
  import PrintTime from "../PrintTime";

  export default {
    name: 'my-printing-history',
    props: ['type', 'dateRange', 'search'],
    mixins: [FileMixin, UserMixin],
    components: {
      PrintTime,
      DateTimeText,
      ModelPreview,
      InfiniteLoading,
      DashboardHistoryActionButtons,
      ContainerLoader,
      JobCost,
      JobFilament,
      JobStatus
    },
    data() {
      return {
        loaded: false,
        jobs: [],
        page: null,
        fields: [
          {key: 'preview', class: 'img'},
          {key: 'filename', class: 'name'},
          {key: 'printTime', class: 'print-time'},
          {key: 'filament', class: 'fill'},
          {key: 'cost', class: 'cost'},
          {key: 'printer_name', class: 'printer-name'},
          {key: 'datetime', class: 'date'},
          {key: 'action', label: '', class: 'action1'}
        ],
        statistic: null,
        statisticLoaded: false
      }
    },
    async created() {
      this.loadHistory();
      this.loadStatistic();
    },
    computed: {
      count() {
        return this.jobs.length;
      },

      hoursPrinted() {
        return Math.round(this.statistic.hours / 3600 * 10) / 10;
      },

      materialsUsed() {
        return Math.round(this.statistic.weight / 1000 * 100) / 100;
      }
    },
    methods: {
      ...mapActions(['fetchJobHistory', 'fetchJobHistoryStatistic']),

      printerTypeAndMaterialText(printer) {
        let res = printer.printer_type;
        if (printer.material_type) {
          res += ', ' + printer.material_type;
        }
        if (printer.layer_height) {
          res += ', ' + printer.layer_height;
        }

        return res;
      },

      loadHistory(callback) {
        const params = {
          isOrganization: this.type === 'org',
          startDate: this.dateRange ? this.dateRange.start : null,
          endDate: this.dateRange ? this.dateRange.end : null,
          search: this.search,
          page: this.page
        };
        this.fetchJobHistory(params).then(({data}) => {
          this.loaded = true;
          if (data.result) {
            for (let i in data.message) {
              this.jobs.push(data.message[i]);
            }
            this.page += 1;
            if (callback) {
              callback(data.message);
            }
          } else {
            if (callback) {
              callback([]);
            }
          }
        });
      },

      loadStatistic() {
        const params = {
          isOrganization: this.type === 'org',
          startDate: this.dateRange ? this.dateRange.start : null,
          endDate: this.dateRange ? this.dateRange.end : null,
          search: this.search
        };
        this.statisticLoaded = false;
        this.fetchJobHistoryStatistic(params).then(({data}) => {
          this.statisticLoaded = true;
          if (data.result) {
            this.statistic = data.message;
          }
        });
      },

      handleInfiniteLoading($state) {
        if (this.jobs.length < 20) {
          $state.complete();
          return;
        }
        this.loadHistory(function(data) {
          if (data.length) {
            $state.loaded();
          } else {
            $state.complete();
          }
        });
      },

      clearLoadedJobs() {
        this.loaded = false;
        this.jobs = [];
        this.page = 0;
      },

      handleOpenTimelaps(jobId) {
        this.$refs['actionButtons' + jobId].handleOpenTimelaps();
      },

      rowClass(item) {
        if (!this.isGcodeFileType(item.file_type_id)) {
          return ['no-cursor-pointer']
        }
        return [];
      },

      handleRowClick(item) {
        if (this.isGcodeFileType(item.file_type_id)) {
          this.$refs['actionButtons' + item.id].handleOpenDetails();
        }
      }
    },

    watch: {
      dateRange: function () {
        this.clearLoadedJobs();
        this.loadHistory();
        this.loadStatistic();
      },

      search: function() {
        this.clearLoadedJobs();
        this.loadHistory();
        this.loadStatistic();
      }
    }
  }
</script>

<style lang="scss">
  @import "../../scss/theme";
    .table--dashboard {
      border: 0;
      padding: 0;
      border-radius: $s-border-radius;

      table {
        tr {


          .printer-name {
            font-weight: 500;
            max-width: 20%;
            margin-left: 6%;
            @media (max-width: 1330px) {
              margin-left: 1%;
            }
          }

          .fill {
            width: 6%
          }

          .print-time, .fill, .cost {
            margin-left: 1%;
            @media (max-width: 1330px) {
              width: 5%;
            }
          }

          .print-time {
            .real {
              padding-right: 8px;
              min-width: 48px;
            }

            .expected {
              padding-left: 8px;
              color: $c-text-gray;
            }
          }

          .name {
            font-weight: 500;
            flex: 0 1 30%;
            @media (max-width: 1330px) {
              flex: unset;
              width: 17%;
            }
          }

          .action1 {
            padding: 0;
            margin-right: 0;
          }

          .date {
            flex: 1 1 10%;

            display: flex;
            flex-wrap: wrap;
            align-items: center;

            @media (max-width: 1330px) {
              flex-grow: 0;
            }

            .edit-time {
              min-width: fit-content;
            }
          }

          @media (max-width: 1100px) {
            .name, .date, .printer-name, .print-time, .fill, .cost {
              max-width: unset;
              min-width: unset;
              width: unset;
              margin-right: 18px;
              flex: unset;

              * {
                min-width: unset;
              }
            }
          }
        }
      }
    }
</style>
